import Box from "@mui/joy/Box";
import Button from "@mui/joy/Button";
import ButtonGroup from "@mui/joy/ButtonGroup";
import Chip from "@mui/joy/Chip";
import Link from "@mui/joy/Link";
import Modal from "@mui/joy/Modal";
import ModalClose from "@mui/joy/ModalClose";
import ModalDialog from "@mui/joy/ModalDialog";
import Sheet from "@mui/joy/Sheet";
import Stack from "@mui/joy/Stack";
import Table from "@mui/joy/Table";
import Textarea from "@mui/joy/Textarea";
import Typography from "@mui/joy/Typography";
import React from "react";

import { PersonId } from "../../../models/person";
import { RPMPatient, RPMStats, RPMStatus } from "../../../models/rpm";
import { useSnackbar } from "../../../shared/components/layout/SnackbarContext";
import { DisplayProofChipSet } from "../../../shared/components/ui/QuestionAnswering/DisplayAnswerFacts";
import { ProofChip } from "../../../shared/components/ui/QuestionAnswering/ProofChip";
import TitleStylizedUnderline from "../../../shared/components/ui/TitleStylizedUnderline";
import { RPMQuartzClientContext } from "../../../shared/services/api/contexts";
import { unixToDayjs } from "../../../shared/utils/time";

export default function RPMInclusion() {
  const rpmQuartzClient = React.useContext(RPMQuartzClientContext);
  const { showSnackbar } = useSnackbar();

  const [currentStatus, setCurrentStatus] = React.useState<RPMStatus>("potential_candidate");
  const [stats, setStats] = React.useState<RPMStats>({
    total_patients: 0,
    enrolled_patients: 0,
    completed_patients: 0,
    rejected_patients: 0,
    potential_candidates: 0,
    unknown_status: 0,
  });
  const [patients, setPatients] = React.useState<RPMPatient[]>([]);
  const [loading, setLoading] = React.useState<boolean>(false);
  const [rejectedPatient, setRejectedPatient] = React.useState<PersonId | null>(null);
  const [rejectionReason, setRejectionReason] = React.useState<string>("");

  const getRPMStats = React.useCallback(async () => {
    try {
      const statsData = await rpmQuartzClient.getRPMStats();
      setStats(statsData);
    } catch (error: any) {
      console.error("Error fetching RPM stats:", error);
      showSnackbar({ text: "Error fetching RPM stats. Please try again later.", color: "danger" });
    }
  }, [rpmQuartzClient, showSnackbar]);

  const fetchPatients = React.useCallback(async () => {
    setLoading(true);
    try {
      const data = await rpmQuartzClient.listPatients(currentStatus);
      setPatients(data.entry as RPMPatient[]);
    } catch (error: any) {
      console.error("Error fetching patients:", error);
      showSnackbar({ text: "Error fetching patients. Please try again later.", color: "danger" });
    } finally {
      setLoading(false);
    }
  }, [rpmQuartzClient, showSnackbar, currentStatus]);

  React.useEffect(() => {
    fetchPatients();
    getRPMStats();
  }, [fetchPatients, getRPMStats]);

  const handleStatusChange = (status: RPMStatus) => {
    setCurrentStatus(status);
  };

  const handleAccept = async (patientId: PersonId) => {
    try {
      await rpmQuartzClient.acceptPatient(patientId);
      showSnackbar({ text: "Patient successfully enrolled in RPM.", color: "success" });
      setPatients((prev) => prev.filter((p) => p.person_id !== patientId));
      getRPMStats();
    } catch (error: any) {
      console.error("Error accepting patient:", error);
      showSnackbar({ text: "Failed to enroll patient. Please try again.", color: "danger" });
    }
  };

  const handleRejectClick = (patientId: PersonId) => {
    setRejectedPatient(patientId);
    setRejectionReason("");
  };

  const handleConfirmReject = async () => {
    if (!rejectedPatient) return;
    try {
      await rpmQuartzClient.rejectPatient(rejectedPatient, { reason: rejectionReason });
      showSnackbar({ text: "Patient successfully rejected.", color: "success" });
      setPatients((prev) => prev.filter((p) => p.person_id !== rejectedPatient));
      setRejectedPatient(null);
      setRejectionReason("");
      getRPMStats();
    } catch (error: any) {
      console.error("Error rejecting patient:", error);
      showSnackbar({ text: "Failed to reject patient. Please try again.", color: "danger" });
    }
  };

  const handleCancelReject = () => {
    setRejectedPatient(null);
    setRejectionReason("");
  };

  const getPrefix = React.useCallback((gender?: string | null) => {
    switch (gender) {
      case "Male":
        return "Mr.";
      case "Female":
        return "Mme.";
      default:
        return "";
    }
  }, []);

  const renderTableBody = () => {
    if (loading) {
      return (
        <tr>
          <td colSpan={6} style={{ textAlign: "center", padding: "16px" }}>
            Loading patients…
          </td>
        </tr>
      );
    }

    if (patients.length === 0) {
      return (
        <tr>
          <td colSpan={6} style={{ textAlign: "center", padding: "16px" }}>
            No {currentStatus.replace("_", " ")} found.
          </td>
        </tr>
      );
    }

    // 3. Normal case: map over patients
    return patients.map((patient) => (
      <tr key={patient.person_id}>
        <td style={{ verticalAlign: "middle" }}>
          <Link
            underline="hover"
            href={`/patient/${patient.person_id}`}
            target="_blank"
            rel="noopener"
          >
            <Stack direction="column" spacing={0.5}>
              <Typography level="title-sm">
                {getPrefix(patient.gender)} {patient.given_name} {patient.family_name}
              </Typography>
              <Typography level="body-sm">
                {patient.birth_date ? unixToDayjs(patient.birth_date)?.format("DD MMM YYYY") : ""}
              </Typography>
              {patient.external_ids
                ?.filter((id) => id.value && id.system !== "ODH")
                .sort((a, b) => (a.system || "").localeCompare(b.system || ""))
                .map((id) => (
                  // use a stable key instead of the array index
                  <Chip key={`${id.system}-${id.value}`} sx={{ mt: 0.5, mr: 0.5 }} size="sm">
                    {id.system}: {id.value}
                  </Chip>
                ))}
            </Stack>
          </Link>
        </td>
        <td style={{ verticalAlign: "middle" }}>
          {patient.last_weight_fact && (
            <DisplayProofChipSet events={[patient.last_weight_fact]} answerType="value-only" />
          )}
        </td>
        <td style={{ verticalAlign: "middle" }}>
          {patient.last_height_fact && (
            <DisplayProofChipSet events={[patient.last_height_fact]} answerType="value-only" />
          )}
        </td>
        <td style={{ verticalAlign: "middle" }}>
          {patient.last_cancer_fact && (
            <DisplayProofChipSet events={[patient.last_cancer_fact]} answerType="concept" />
          )}
        </td>
        <td style={{ verticalAlign: "middle" }}>
          {patient.last_metastasis_facts && (
            <DisplayProofChipSet events={patient.last_metastasis_facts} answerType="concept" />
          )}
        </td>
        <td style={{ verticalAlign: "middle" }}>
          {patient.last_treatment_episode && (
            <ProofChip
              name={patient.last_treatment_episode.episode_object_name}
              probability={patient.last_treatment_episode.probability}
              eventSources={patient.last_treatment_episode.source}
            />
          )}
        </td>
        {currentStatus === "potential_candidate" && (
          <td style={{ textAlign: "right", verticalAlign: "middle" }}>
            <ButtonGroup spacing="0.5rem" aria-label="actions rpm">
              <Button
                color="success"
                onClick={() => handleAccept(patient.person_id)}
                title="Enroll in RPM"
                size="sm"
              >
                Enroll
              </Button>
              <Button
                color="danger"
                onClick={() => handleRejectClick(patient.person_id)}
                title="Reject candidate"
              >
                Reject
              </Button>
            </ButtonGroup>
          </td>
        )}
      </tr>
    ));
  };

  return (
    <Box sx={{ p: 4 }}>
      <Stack direction="column" spacing={2}>
        <Typography level="h2">
          <TitleStylizedUnderline color="red">RPM Inclusion</TitleStylizedUnderline>
        </Typography>

        <ButtonGroup spacing="0.5rem" aria-label="status filter">
          <Button
            variant={currentStatus === "potential_candidate" ? "solid" : "outlined"}
            onClick={() => handleStatusChange("potential_candidate")}
          >
            {/* Potential candidates ({stats.potential_candidates}) */}
            Potential Candidates ({stats.potential_candidates})
          </Button>
          <Button
            variant={currentStatus === "enrolled" ? "solid" : "outlined"}
            onClick={() => handleStatusChange("enrolled")}
          >
            Enrolled ({stats.enrolled_patients})
          </Button>
          <Button
            variant={currentStatus === "completed" ? "solid" : "outlined"}
            onClick={() => handleStatusChange("completed")}
          >
            Completed ({stats.completed_patients})
          </Button>
          <Button
            variant={currentStatus === "rejected" ? "solid" : "outlined"}
            onClick={() => handleStatusChange("rejected")}
          >
            Rejected ({stats.rejected_patients})
          </Button>
        </ButtonGroup>

        <Sheet
          variant="outlined"
          sx={{
            borderRadius: "sm",
            height: "70vh",
            overflow: "auto",
            position: "relative",
          }}
        >
          <Table
            aria-labelledby="rpmInclusionTable"
            stickyHeader
            hoverRow
            sx={{
              minWidth: { xs: "600px", sm: "800px", md: "1000px" },
              "--TableCell-headBackground": "var(--joy-palette-background-level1)",
              "--Table-headerUnderlineThickness": "1px",
              "--TableRow-hoverBackground": "var(--joy-palette-background-level1)",
              "--TableCell-paddingY": "4px",
              "--TableCell-paddingX": "8px",
            }}
          >
            <thead>
              <tr>
                <th style={{ padding: "12px 6px" }}>Patient</th>
                <th style={{ padding: "12px 6px", width: "90px" }}>Weight</th>
                <th style={{ padding: "12px 6px", width: "90px" }}>Height</th>
                <th style={{ padding: "12px 6px" }}>Cancer</th>
                <th style={{ padding: "12px 6px" }}>Metastasis</th>
                <th style={{ padding: "12px 6px" }}>Treatment</th>
                {currentStatus === "potential_candidate" && (
                  <th style={{ textAlign: "right", padding: "12px 6px", width: 170 }}></th>
                )}
              </tr>
            </thead>
            <tbody>{renderTableBody()}</tbody>
          </Table>
        </Sheet>
      </Stack>

      <Modal
        open={rejectedPatient !== null}
        onClose={handleCancelReject}
        sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}
      >
        <ModalDialog color="danger" variant="outlined" sx={{ width: 400 }}>
          <ModalClose />
          <Typography level="h4" sx={{ mb: 2 }}>
            Confirm Rejection
          </Typography>
          <Typography level="body-md" sx={{ mb: 2 }}>
            Please provide a reason for rejecting this patient:
          </Typography>
          <Textarea
            placeholder="Rejection reason..."
            minRows={3}
            value={rejectionReason}
            onChange={(e) => setRejectionReason(e.target.value)}
            sx={{ mb: 2 }}
          />
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
            <Button variant="plain" color="neutral" onClick={handleCancelReject}>
              Cancel
            </Button>
            <Button
              variant="solid"
              color="danger"
              disabled={rejectionReason.trim() === ""}
              onClick={handleConfirmReject}
            >
              Reject
            </Button>
          </Box>
        </ModalDialog>
      </Modal>
    </Box>
  );
}
